import React, { useEffect, useMemo, useState } from 'react';
import { fetchBookings, fetchPayments } from '../api';

function CashFlow() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totals, setTotals] = useState({ inTotal: 0, outTotal: 0 });
  const [payments, setPayments] = useState([]);

  const { startDate, endDate } = useMemo(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { startDate: fmt(firstDay), endDate: fmt(lastDay) };
  }, []);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const [unpaid, sent, payments] = await Promise.all([
          fetchBookings({ startDate, endDate, comissionStatus: 'unpaid' }),
          fetchBookings({ startDate, endDate, comissionStatus: 'sent' }),
          fetchPayments({ status: 'paid', start_date: `${startDate}T12:00:00Z`, end_date: `${endDate}T12:00:00Z` })
        ]);

        const combined = [...(unpaid || []), ...(sent || [])];
        const dedupedById = Object.values(
          combined.reduce((acc, b) => {
            const key = b.atendimento_id || b.id || `${b.profissional || ''}-${b.datetimeAtendimento || b.dataAtendimento || ''}`;
            if (!acc[key]) acc[key] = b;
            return acc;
          }, {})
        );

        const realized = dedupedById.filter((b) => (b.status || '') === 'Realizado');
        const getBookingTotal = (bk) => {
          if (bk && bk.valorTotal !== null && bk.valorTotal !== undefined && bk.valorTotal !== '') {
            const n = parseFloat(bk.valorTotal);
            if (!Number.isNaN(n)) return n;
          }
          if (Array.isArray(bk?.procedimentosList) && bk.procedimentosList.length) {
            return bk.procedimentosList.reduce((s, p) => s + (p && p.valor ? parseFloat(p.valor) : 0), 0);
          }
          if (Array.isArray(bk?.procedimentos) && bk.procedimentos.length) {
            return bk.procedimentos.reduce((s, p) => s + (p && p.valor ? parseFloat(p.valor) : 0), 0);
          }
          return 0;
        };
        const inTotal = realized.reduce((sum, b) => sum + getBookingTotal(b), 0);

        const outTotal = (payments || []).reduce((sum, p) => sum + (p.amount ? parseFloat(p.amount) : 0), 0);

        setPayments(payments || []);
        setTotals({ inTotal, outTotal });
      } catch (e) {
        setError('Falha ao carregar dados.');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [startDate, endDate]);

  const diff = useMemo(() => totals.inTotal - totals.outTotal, [totals]);

  const costsByCategory = useMemo(() => {
    const grouped = payments.reduce((acc, p) => {
      const cat = p.categoria || 'Sem categoria';
      if (!acc[cat]) acc[cat] = 0;
      acc[cat] += p.amount ? parseFloat(p.amount) : 0;
      return acc;
    }, {});
    console.log(grouped);
    return Object.entries(grouped)
      .map(([categoria, total]) => ({ categoria, total }))
      .sort((a, b) => b.total - a.total);
  }, [payments]);

  return (
    <div className="financeiro-container">
      <h2>Fluxo de caixa (último mês)</h2>
      <div style={{ marginBottom: 12, color: '#555' }}>
        Período: {startDate} a {endDate}
      </div>
      {loading ? (
        <div className="loading">Carregando...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
          <div className="card">
            <div className="card-title">Entradas (Realizado)</div>
            <div className="card-value">R$ {totals.inTotal.toFixed(2)}</div>
          </div>
          <div className="card">
            <div className="card-title">Saídas (Pagos)</div>
            <div className="card-value">R$ {totals.outTotal.toFixed(2)}</div>
          </div>
          <div className="card">
            <div className="card-title">Resultado</div>
            <div className="card-value" style={{ color: diff >= 0 ? '#2e7d32' : '#d32f2f' }}>R$ {diff.toFixed(2)}</div>
          </div>
        </div>
      )}

      {!loading && !error && costsByCategory.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h3>Saídas por Categoria</h3>
          <table className="financeiro-table" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>Categoria</th>
                <th style={{ textAlign: 'right' }}>Valor</th>
              </tr>
            </thead>
            <tbody>
              {costsByCategory.map((row) => (
                <tr key={row.categoria}>
                  <td>{row.categoria}</td>
                  <td style={{ textAlign: 'right' }}>R$ {row.total.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 'bold' }}>
                <td>Total</td>
                <td style={{ textAlign: 'right' }}>R$ {totals.outTotal.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

export default CashFlow;


